import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import {
  IBooking,
  ICancelBookingInputType,
  ICoach,
  ICoachBooking,
  ICourt,
  IEventPrice,
  IGuest,
  IUser,
} from "../../../types/api";
import { getUserByMemberId } from "../../../api/users/users";
import { getGuestByMemberId } from "../../../api/guests/guests";
import { getCoachByID } from "../../../api/coaches/coaches";
import { capitalizeWords, formatDateToSwedish } from "../../../shared/utility";
import { cancelBooking } from "../../../api/bookings/bookings";
import { toast } from "react-toastify";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";

interface ICoachShowBookedInfoPopupProps {
  onClose: () => void;
  showPopup: boolean;
  onConfirm: () => void;
  booking?: ICoachBooking;
  courts: ICourt[];
}

function CoachShowBookedInfoPopup(props: ICoachShowBookedInfoPopupProps) {
  const { booking, showPopup } = props;
  const [mainBookerData, setMainBookerData] = useState<IUser>();
  const [coPlayersData, setCoPlayersData] = useState<IUser[]>();
  const [guestsData, setGuestsData] = useState<IGuest[]>();
  const [coachData, setCoachData] = useState<ICoach>();
  const [loading, setLoading] = useState(false);
  const [checkedInBookingCompare, setCheckedInBookingCompare] =
    useState<ICoachBooking>();

  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const handleClose = () => {
    props.onClose();
  };

  const handleFetchMainBookerData = async () => {
    if (booking === undefined) return;
    const mainBooker = booking.member_id;
    const response = await getUserByMemberId(mainBooker);
    setMainBookerData(response);
  };
  const handleFetchGuestsData = async () => {
    if (booking === undefined) return;
    const guestList: IGuest[] = [];
    const guests = booking.guests;
    const response = await Promise.all(
      guests.map(async (guest) => {
        if (guest.member_id !== "guest") {
          return await getGuestByMemberId(guest.member_id);
        }
      })
    );
    response.forEach((res) => {
      if (res) {
        guestList.push(res);
      }
    });
    setGuestsData(guestList);
  };

  const handleFetchCoPlayersData = async () => {
    if (booking === undefined) return;
    const coPlayers = booking.co_players;
    const response = await Promise.all(
      coPlayers.map(async (coPlayer) => {
        return await getUserByMemberId(coPlayer.member_id);
      })
    );
    setCoPlayersData(response);
  };

  const handleFetchCoachData = async () => {
    if (booking === undefined) return;
    const coach = booking.coach_id[0];
    const response = await getCoachByID(coach);

    setCoachData(response);
  };

  const handleCancelBooking = async () => {
    const booking = props.booking;
    if (!booking) return;
    setLoading(true);
    const coachToCancle = booking.coach_id.length > 0 ? booking.coach_id : [];
    const bookingToCancel: ICancelBookingInputType = {
      court_id: booking.court_id,
      datetime_start: booking.datetime_start,
      booking_id: booking.booking_id,
      slot_index: booking.slot_index,
      coach_id: coachToCancle,
    };
    try {
      const cancelBookingResponse = await cancelBooking(
        bookingToCancel,
        "admin",
        undefined,
        undefined,
        true
      );
      if (cancelBookingResponse) {
        toast.success("Bokning avbokad");
        setShowConfirmationPopup(false);
        props.onConfirm();
        handleClose();
      }
    } catch (error: any) {
      toast.error("Error cancelling booking" + error);
    }finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (booking !== undefined) {
      handleFetchMainBookerData();
      handleFetchCoPlayersData();
      handleFetchGuestsData();
      if (booking.coach_id.length > 0) {
        handleFetchCoachData();
      }
    }
  }, [showPopup]);

  const startDate = new Date(booking?.datetime_start || "");
  const { weekday, dateAndMonth, hour } = formatDateToSwedish(
    new Date(startDate)
  );

  function accumulatePrices(finalPrices: IEventPrice[]): string {
    const total = finalPrices.reduce((sum, item) => sum + item.price, 0);
    return total.toString();
  }
  const dateString = `${weekday} ${dateAndMonth}`;
  const courtName = props.courts.find(
    (c) => c.court_id === booking?.court_id
  )?.name;
  let timeCourtString = "";

  if (startDate) {
    const startTime = new Date(startDate);
    const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add one hour

    const formatTime = (date: Date) => {
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    };

    timeCourtString = `${formatTime(startTime)} - ${formatTime(
      endTime
    )} ${courtName}`;
  }

  /**
   * Once the booking has started there is nothing useful left to cancel, so
   * the action goes away rather than failing on the server.
   *
   * Judged on the start, which is also the only time the booking item actually
   * carries — there is no datetime_end, and the end shown above is a
   * start + 60 min guess that is wrong for the 50 and 55 minute formats.
   */
  const bookingHasStarted =
    !Number.isNaN(startDate.getTime()) && startDate.getTime() <= Date.now();

  const userString = `${
    capitalizeWords(mainBookerData?.first_name || "Loading") +
    " " +
    capitalizeWords(mainBookerData?.last_name || "Loading") +
    " (" +
    mainBookerData?.member_id +
    ")"
  }`;

  const bookingToLoop = checkedInBookingCompare || (booking as ICoachBooking);
  if (bookingToLoop === undefined) {
    return null;
  }
  const coachPrize =
    bookingToLoop.coach_id.length > 0
      ? bookingToLoop.final_price.find((event) =>
          event.event.includes("coach_price")
        )?.price
      : 0;

  return (
    <Popup
      onClose={handleClose}
      showPopup={props.showPopup}
      leftTopElement={<span className="text-m">Information bokning </span>}
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form less-gap"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Datum</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{dateString}</span>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text ">Tid + Bana</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{timeCourtString}</span>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Huvudbokare</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span> {userString}</span>
                </div>
              </div>
              <div className="general-popup-flex-between align-top">
                <span className="text-m primary-text">Medspelare</span>
                <span className="text-m primary-text text-svg-wrapper">
                  <div className="chosen-players">
                    {coPlayersData &&
                      coPlayersData.map((player, index) => {
                        const { first_name, last_name } = player;
                        const playerString = `${capitalizeWords(
                          first_name
                        )} ${capitalizeWords(last_name)} (${player.member_id})`;
                        const priceForPlayer = bookingToLoop.final_price.find(
                          (event) => {
                            return event.event.includes(player.member_id);
                          }
                        );
                        const priceString = priceForPlayer?.price || "0";

                        return (
                          <div key={index} className="chosen-player">
                            {playerString}
                          </div>
                        );
                      })}
                  </div>
                </span>
              </div>
              {bookingToLoop.guests.length > 0 && (
                <div className="general-popup-flex-between align-top">
                  <span className="text-m primary-text">Gäster</span>
                  <span className="text-m primary-text text-svg-wrapper">
                    <div className="chosen-players">
                      {bookingToLoop.guests.map((guest, index) => {
                        const priceForPlayer = bookingToLoop.final_price.find(
                          (event) => {
                            return event.event.includes("guest");
                          }
                        );
                        const priceString = priceForPlayer?.price || "0";

                        let guestToShow = guestsData?.find((g) =>
                          g.member_id !== "guest"
                            ? g.member_id === guest.member_id
                            : false
                        );

                        return (
                          <div key={index} className="chosen-player">
                            {guestToShow
                              ? capitalizeWords(guestToShow.first_name) +
                                " " +
                                capitalizeWords(guestToShow.last_name)
                              : `Gäst ${index + 1}`}
                            {bookingToLoop.guests[index].status ===
                            "CHECKED_IN" ? (
                              <>
                                <span className="price-for-member">
                                  {priceString} kr
                                </span>
                                <span className="checked-in">Incheckad</span>
                              </>
                            ) : (
                              <>
                                <span className="price-for-member">
                                  {priceString} kr
                                </span>
                                <span className="">
                                  <div> inte incheckad</div>
                                </span>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </span>
                </div>
              )}
              {booking !== undefined && booking.coach_id.length > 0 && (
                <div className="general-popup-flex-between align-top">
                  <span className="text-m primary-text">Tränare</span>
                  <span className="text-m primary-text">
                    {bookingToLoop.coach_id.map((coach) => {
                      const coachString =
                        capitalizeWords(
                          coachData?.first_name + " " + coachData?.last_name
                        ) +
                        " (" +
                        coach +
                        ")";
                      return (
                        <div>
                          {coachString}
                          {/* {coachPrize} kr */}
                        </div>
                      );
                    })}
                  </span>
                </div>
              )}
            </div>

            {bookingHasStarted ? (
              <span className="text-s secondary-text booking-ended-note">
                Tiden har passerat och kan inte avbokas.
              </span>
            ) : (
              <button
                className="deleteButton"
                onClick={() => setShowConfirmationPopup(true)}
              >
                Avboka
              </button>
            )}
          </div>
        </form>
      </div>

      <HandleConfirmationPopup
        showPopup={showConfirmationPopup}
        onClose={() => setShowConfirmationPopup(false)}
        onConfirm={handleCancelBooking}
        questionText="Är du säker på att du vill avboka bokningen?"
        confirmText="Ja"
        loading={loading}
      />
    </Popup>
  );
}

export default CoachShowBookedInfoPopup;
